import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import whiteYfsLogo from "../../assets/company-ghg-yfs/white_YFS.png";
import "./yfs-label-lovable.css";

const fmtCo2 = (value) => {
  if (value === undefined || value === null) return "N/A";
  if (typeof value === "number") return value.toFixed(2);
  return String(value);
};

const Field = ({ label, value, twoLine = false }) => (
  <div className="company-ghg-yfs__yfs-field">
    <span className="company-ghg-yfs__yfs-field-label">{label}</span>
    <span
      className={`company-ghg-yfs__yfs-field-value${
        twoLine ? " company-ghg-yfs__yfs-field-value--multiline" : ""
      }`}
    >
      {value ?? "N/A"}
    </span>
  </div>
);

const YFSLabelLovableCard = ({ companyData }) => {
  const { t } = useTranslation();

  const reportingYear =
    new URLSearchParams(window.location.search).get("year") ||
    new Date().getFullYear();

  const fields = useMemo(() => {
    if (!companyData) return [];

    const envUnit = (key) => {
      if (key === "max_energy_used") return "";
      if (key.includes("distance")) return "km";
      if (key === "total_energy_used") return "mj";
      if (key.includes("water")) return "L";
      return "kg";
    };

    const envValue = (key) => {
      const raw = companyData[key];
      const unit = envUnit(key);
      if (raw === undefined || raw === null || raw === "") return "N/A";
      return unit ? `${raw} ${unit}` : String(raw);
    };

    return [
      { label: t("businessName"), value: companyData.business_name || "N/A" },
      { label: t("address"), value: companyData.address || "N/A", twoLine: true },
      { label: t("email"), value: companyData.business_email || "N/A" },
      { label: t("website"), value: companyData.website || "N/A" },
      {
        label: t("totalFacilities"),
        value: companyData.total_facilities ?? "N/A",
      },
      {
        label: t("totalEmployees"),
        value: companyData.total_employees ?? "N/A",
      },
      { label: "Reporting year", value: String(reportingYear) },
      {
        label: t("scope1"),
        value: `${fmtCo2(companyData.scope_1)} (CO₂e)`,
      },
      {
        label: t("scope2"),
        value: `${fmtCo2(companyData.scope_2)} (CO₂e)`,
      },
      {
        label: t("scope3"),
        value: `${fmtCo2(companyData.scope_3)} (CO₂e)`,
      },
      {
        label: t("totalCO2"),
        value: `${fmtCo2(companyData.total_business_co2)} (CO₂e)`,
      },
      {
        label: t("outsideScope"),
        value: `${fmtCo2(companyData.total_outside_scope)} (CO₂e)`,
      },
      { label: t("virginMaterials"), value: envValue("total_virgin") },
      { label: t("recycledMaterials"), value: envValue("total_recycle") },
      { label: t("waterUsed"), value: envValue("water_estimate") },
      { label: t("surplusMaterials"), value: envValue("total_surplus_material") },
      { label: t("packaging"), value: envValue("total_packaging") },
      { label: t("transportDistance"), value: envValue("transport_distance") },
      { label: t("energyUsed"), value: envValue("total_energy_used") },
      { label: t("maxEnergyUsed"), value: envValue("max_energy_used") },
    ];
  }, [companyData, reportingYear, t]);

  const name = companyData?.business_name || "—";

  return (
    <div className="company-ghg-yfs__yfs-card">
      <div className="company-ghg-yfs__yfs-card-header">{name}</div>

      <div className="company-ghg-yfs__yfs-verification">
        <div className="company-ghg-yfs__yfs-verification-title">Verification</div>
        <div className="company-ghg-yfs__yfs-verification-legend">
          <span>
            <span className="company-ghg-yfs__yfs-dot company-ghg-yfs__yfs-dot--third" />
            Third party
          </span>
          <span>
            <span className="company-ghg-yfs__yfs-dot company-ghg-yfs__yfs-dot--supplier" />
            Supplier
          </span>
          <span>
            <span className="company-ghg-yfs__yfs-dot company-ghg-yfs__yfs-dot--internal" />
            Internal
          </span>
        </div>
      </div>

      <div className="company-ghg-yfs__yfs-fields">
        {fields.map((field) => (
          <Field
            key={field.label}
            label={field.label}
            value={field.value}
            twoLine={field.twoLine}
          />
        ))}
      </div>

      <div className="company-ghg-yfs__yfs-card-footer">
        <img src={whiteYfsLogo} alt="" className="company-ghg-yfs__yfs-footer-logo" />
        <span>YFS LABEL</span>
      </div>
    </div>
  );
};

export default YFSLabelLovableCard;
